import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import * as signalR from '@microsoft/signalr';
import { Observable } from 'rxjs';
import { TokenExtensions } from '../extensions/token-extensions';
import { TranslateService } from '@ngx-translate/core';
import { ImpersonateService } from 'app/profile/impersonate-user/impersonate.service';

export enum SIGNAL_R_EVENTS {
  JoinRoom = 'JoinRoom',
  Send = 'Send',
  CreateRoom = 'CreateRoom',
  CloseRoom = 'CloseRoom',
  RoomUsers = 'RoomUsers'
}

export enum SIGNAL_R_LISTENERS {
  ReceiveNotification = 'ReceiveNotification',
  ChatHubMessageNotification = 'ChatHubMessageNotification',
  ChatHubAddActiveRoomNotification = 'ChatHubAddActiveRoomNotification',
  ChatHubRoomUsersNotification = 'ChatHubRoomUsersNotification',
  ChatHubDeleteActiveRoomNotification = 'ChatHubDeleteActiveRoomNotification'
}

@Injectable({
  providedIn: 'root',
})

export class SignalRService {
  private notificationsHubConnection!: signalR.HubConnection;
  private chatHubConnection!: signalR.HubConnection;
  private caseLockHubConnection!: signalR.HubConnection;
  private idToken: any;


  constructor(private translate: TranslateService, private impersonateService: ImpersonateService) {
    this.idToken = TokenExtensions.getTokenFromLocalStorage();

    if (!this.idToken?.secret) {
      const errorMessage = this.translate.instant('messages.token_missing_or_invalid');
      console.error(errorMessage);
      return;
    }

    this.impersonateService.impersonatedUser = JSON.parse(sessionStorage.getItem('impersonatedUser') as string);

    // in-app notifications
    const url: string = `/api/hubs/notifications${impersonateService.impersonatedUser ? `?Impersonated-Email=${impersonateService.impersonatedUser?.email}` : ''}`;
    this.notificationsHubConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${environment.clientUrl}${url}`, {
        accessTokenFactory: () => this.idToken.secret,
      })
      .withAutomaticReconnect([1000, 2000, 3000, 5000, 10000])
      .configureLogging(signalR.LogLevel.Information)
      .build();

    // chat notifications
    this.chatHubConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${environment.clientUrl}/api/hubs/chat`, {
        accessTokenFactory: () => this.idToken.secret,
      })
      .withAutomaticReconnect([1000, 2000, 3000, 5000, 10000])
      .configureLogging(signalR.LogLevel.Information)
      .build();

    // case locking
    this.caseLockHubConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${environment.clientUrl}/api/hubs/lock`, {
        accessTokenFactory: () => this.idToken.secret,
      })
      .withAutomaticReconnect([1000, 2000, 3000, 5000, 10000])
      .configureLogging(signalR.LogLevel.Information)
      .build();
  }

  startInAppNotificationsConnection(): Observable<void> {
    // in-app notifications
    return new Observable<void>((observer) => {
      this.notificationsHubConnection
        .start()
        .then(() => {
          observer.next();
          observer.complete();
        })
        .catch((error) => {
          observer.error(error);
        });
    });
  }

  startChatNotificationsConnection(): Observable<void> {
    // chat notifications
    return new Observable<void>((observer) => {
      this.chatHubConnection
        .start()
        .then(() => {
          observer.next();
          observer.complete();
        })
        .catch((error) => {
          observer.error(error);
        });
    });
  }

  startCaseLockingConnection(): Observable<void> {
    // case locking
    return new Observable<void>((observer) => {
      this.caseLockHubConnection
        .start()
        .then(() => {
          observer.next();
          observer.complete();
        })
        .catch((error) => {
          observer.error(error);
        });
    });
  }

  listenToNotificationsEvent<T>(eventName: string): Observable<T> {
    return new Observable<T>((observer) => {
      this.notificationsHubConnection.on(eventName, (data: T) => {
        observer.next(data);
      });
    });
  }

  listenToChatNotificationsEvent<T>(eventName: string): Observable<T> {
    return new Observable<T>((observer) => {
      this.chatHubConnection.on(eventName, (data: T) => {
        observer.next(data);
      });
    });
  }

  invokeEvent(eventName: string, ...args: any[]): Observable<any> {
    return new Observable((observer) => {
      this.chatHubConnection.invoke(eventName, ...args)
        .then(() => {
          observer.next({ success: true });
          observer.complete();
        })
        .catch((err) => {
          observer.next({ success: false, error: err });
          observer.complete();
        });
    });
  }
}
